import { A } from '@adornis/base/env-info.js';
import { guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import { COMPANY_REGISTRATION_CLOSED_ERROR, submitCompanyRegistration } from '../../../api/company-registration.js';
import { subscribeMatchingWeekSettings } from '../../../api/settings.js';
import { isValidSingleEmail } from '../../../shared/email.js';
import { useScrollToError } from '../../components/format-form-error.js';
import { UserToolbar } from '../../components/user-toolbar.js';

const INFO_LINK = 'https://ics-rm.de/arbeitgeber/matching-week-unternehmen/';

export const CompanyRegistrationForm = stateful(() => {
  const [companyName, setCompanyName] = useState('');
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPosition, setContactPosition] = useState('');
  const [address, setAddress] = useState('');
  const [sector, setSector] = useState('');
  const [website, setWebsite] = useState('');
  const [services, setServices] = useState('');
  const [openPositions, setOpenPositions] = useState('');
  const [participationReason, setParticipationReason] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [participationAgreementAccepted, setParticipationAgreementAccepted] = useState(false);

  const settingsQuery = useQuery(
    () =>
      subscribeMatchingWeekSettings()({
        participationAgreementFileId: 1,
        participationAgreementDate: 1,
        companyRegistrationClosed: 1,
      }),
    [],
  );
  const agreementFileId = settingsQuery.value?.participationAgreementFileId ?? null;
  const agreementDate = settingsQuery.value?.participationAgreementDate ?? '';
  const companyRegistrationClosed = settingsQuery.value?.companyRegistrationClosed === true;

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useScrollToError(error);

  const handleCompanyRegistrationError = (submissionError: unknown) => {
    if (submissionError instanceof Error && submissionError.message === COMPANY_REGISTRATION_CLOSED_ERROR) {
      void showDialog({ content: () => m.company_registration_closed_error() });
      return;
    }
    setError(submissionError instanceof Error ? submissionError.message : m.submit_application_unknown_error());
  };

  const handleSubmit = async () => {
    if (companyRegistrationClosed) {
      handleCompanyRegistrationError(new Error(COMPANY_REGISTRATION_CLOSED_ERROR));
      return;
    }

    const allFilled =
      companyName.trim() &&
      address.trim() &&
      sector.trim() &&
      services.trim() &&
      website.trim() &&
      openPositions.trim() &&
      participationReason.trim() &&
      contactFirstName.trim() &&
      contactLastName.trim() &&
      contactPosition.trim() &&
      contactEmail.trim();
    if (!allFilled) {
      setError('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }
    if (!isValidSingleEmail(contactEmail)) {
      setError('Bitte geben Sie genau eine gültige E-Mail-Adresse ein.');
      return;
    }
    if (!termsAccepted) {
      setError('Bitte akzeptieren Sie die Nutzungsbedingungen für Unternehmen.');
      return;
    }
    if (!participationAgreementAccepted) {
      setError('Bitte stimmen Sie der Teilnahmevereinbarung zu.');
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const submissionSucceeded = await guardDialog(
        () =>
          submitCompanyRegistration(
            companyName,
            contactFirstName,
            contactLastName,
            contactEmail,
            address,
            sector,
            website,
            services,
            openPositions,
            participationReason,
            contactPosition,
            termsAccepted,
            participationAgreementAccepted,
          )(),
        {
          error: submissionError => {
            handleCompanyRegistrationError(submissionError);
          },
        },
      );
      if (submissionSucceeded === true) setSubmitted(true);
    } catch (submissionError) {
      console.error(submissionError);
      handleCompanyRegistrationError(submissionError);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return html`
      <div class="h-full bg-base-200 flex flex-col overflow-y-auto">
        <div class="flex-1 max-w-3xl mx-auto px-4 py-10 w-full">
          <div class="flex items-center justify-between mb-6">
            <a href="/" class="btn btn-ghost p-0">
              <span class="icon-[material-symbols--arrow-back]"></span>
              ${m.cancel()}
            </a>
            ${UserToolbar()}
          </div>

          <!-- Success icon with animation -->
          <div class="flex justify-center">
            <div class="rounded-full bg-success/10 p-6 animate-fade-in">
              <div class="rounded-full bg-success/20 p-4">
                <div class="rounded-full bg-success p-4 shadow-lg shadow-success/30">
                  <svg class="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div class="card bg-base-100 shadow-xl">
            <div class="card-body items-center text-center space-y-4 py-8">
              <p class="text-lg max-w-prose">${m.company_registration_success_message()}</p>
              <p class="text-sm text-base-content/70">
                ${m.company_registration_success_info_link_text()}
                <a href="${INFO_LINK}" target="_blank" rel="noopener" class="link link-primary">${INFO_LINK}</a>
              </p>
              <p class="text-sm text-base-content/70">${m.company_registration_success_contact()}</p>
            </div>
          </div>

          <style>
            @keyframes fade-in {
              0% {
                opacity: 0;
                transform: scale(0.9);
              }
              100% {
                opacity: 1;
                transform: scale(1);
              }
            }
            .animate-fade-in {
              animation: fade-in 0.6s ease-out forwards;
            }
          </style>
        </div>

        <div class="py-4 border-t border-base-300">
          <div class="flex justify-center gap-4 text-sm text-base-content/70">
            <a href="${A.absoluteUrl('/privacy')}" class="hover:text-base-content transition-colors"
              >${m.privacy_policy()}</a
            >
            <a href="${A.absoluteUrl('/imprint')}" class="hover:text-base-content transition-colors">${m.imprint()}</a>
          </div>
        </div>
      </div>
    `;
  }

  return html`
    <div class="h-full bg-base-200 flex flex-col overflow-y-auto">
      <div class="flex-1 max-w-3xl mx-auto px-4 py-10 w-full">
        <div class="flex items-center justify-between mb-6">
          <a href="/" class="btn btn-ghost p-0">
            <span class="icon-[material-symbols--arrow-back]"></span>
            ${m.cancel()}
          </a>
          ${UserToolbar()}
        </div>

        <div class="space-y-5">
          <div class="space-y-2">
            <h1 class="text-2xl font-bold">${m.company_registration_page_title()}</h1>
            <p class="text-base-content/70">${m.company_registration_intro()}</p>
          </div>

          ${
            companyRegistrationClosed
              ? html`
                  <div class="alert alert-warning shadow-lg">
                    <span class="icon-[material-symbols--warning] w-6 h-6"></span>
                    <span>${m.company_registration_closed_notice()}</span>
                  </div>
                `
              : nothing
          }
          ${
            error
              ? html`<div class="alert alert-error" data-error-anchor>
                  <span class="icon-[material-symbols--error] w-6 h-6"></span>
                  <span>${error}</span>
                </div>`
              : nothing
          }

          <div class="card bg-base-100 shadow-xl">
            <div class="card-body gap-6">
              <!-- Section 1: Company Info -->
              <div class="space-y-3">
                <h3 class="text-lg font-semibold">${m.company_registration_section_company()}</h3>
                <div class="form-control">
                  <label class="label"
                    ><span class="label-text">${m.company_registration_companyName_label()} *</span></label
                  >
                  <input
                    type="text"
                    class="input input-bordered w-full"
                    placeholder=${m.company_registration_companyName_placeholder()}
                    .value=${companyName}
                    @input=${(e: InputEvent) => setCompanyName((e.target as HTMLInputElement).value)}
                  />
                </div>
                <div class="form-control">
                  <label class="label"
                    ><span class="label-text">${m.company_registration_address_label()} *</span></label
                  >
                  <input
                    type="text"
                    class="input input-bordered w-full"
                    placeholder=${m.company_registration_address_placeholder()}
                    .value=${address}
                    @input=${(e: InputEvent) => setAddress((e.target as HTMLInputElement).value)}
                  />
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div class="form-control">
                    <label class="label"
                      ><span class="label-text">${m.company_registration_sector_label()} *</span></label
                    >
                    <input
                      type="text"
                      class="input input-bordered w-full"
                      placeholder=${m.company_registration_sector_placeholder()}
                      .value=${sector}
                      @input=${(e: InputEvent) => setSector((e.target as HTMLInputElement).value)}
                    />
                  </div>
                  <div class="form-control">
                    <label class="label"
                      ><span class="label-text">${m.company_registration_services_label()} *</span></label
                    >
                    <input
                      type="text"
                      class="input input-bordered w-full"
                      placeholder=${m.company_registration_services_placeholder()}
                      .value=${services}
                      @input=${(e: InputEvent) => setServices((e.target as HTMLInputElement).value)}
                    />
                  </div>
                </div>
                <div class="form-control">
                  <label class="label"
                    ><span class="label-text">${m.company_registration_website_label()} *</span></label
                  >
                  <input
                    type="text"
                    class="input input-bordered w-full"
                    placeholder=${m.company_registration_website_placeholder()}
                    .value=${website}
                    @input=${(e: InputEvent) => setWebsite((e.target as HTMLInputElement).value)}
                  />
                </div>
              </div>

              <div class="divider my-0"></div>

              <!-- Section 2: Free Text -->
              <div class="space-y-3">
                <h3 class="text-lg font-semibold">${m.company_registration_section_freetext()}</h3>
                <div class="form-control">
                  <label class="label"
                    ><span class="label-text">${m.company_registration_openPositions_label()} *</span></label
                  >
                  <textarea
                    class="textarea textarea-bordered w-full"
                    rows="3"
                    placeholder=${m.company_registration_openPositions_placeholder()}
                    .value=${openPositions}
                    @input=${(e: InputEvent) => setOpenPositions((e.target as HTMLTextAreaElement).value)}
                  ></textarea>
                </div>
                <div class="form-control">
                  <label class="label"
                    ><span class="label-text">${m.company_registration_participationReason_label()} *</span></label
                  >
                  <textarea
                    class="textarea textarea-bordered w-full"
                    rows="3"
                    placeholder=${m.company_registration_participationReason_placeholder()}
                    .value=${participationReason}
                    @input=${(e: InputEvent) => setParticipationReason((e.target as HTMLTextAreaElement).value)}
                  ></textarea>
                </div>
              </div>

              <div class="divider my-0"></div>

              <!-- Section 3: Contact Person -->
              <div class="space-y-3">
                <h3 class="text-lg font-semibold">${m.company_registration_section_contact()}</h3>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div class="form-control">
                    <label class="label"
                      ><span class="label-text">${m.company_registration_contactFirstName_label()} *</span></label
                    >
                    <input
                      type="text"
                      class="input input-bordered w-full"
                      placeholder=${m.company_registration_contactFirstName_placeholder()}
                      .value=${contactFirstName}
                      @input=${(e: InputEvent) => setContactFirstName((e.target as HTMLInputElement).value)}
                    />
                  </div>
                  <div class="form-control">
                    <label class="label"
                      ><span class="label-text">${m.company_registration_contactLastName_label()} *</span></label
                    >
                    <input
                      type="text"
                      class="input input-bordered w-full"
                      placeholder=${m.company_registration_contactLastName_placeholder()}
                      .value=${contactLastName}
                      @input=${(e: InputEvent) => setContactLastName((e.target as HTMLInputElement).value)}
                    />
                  </div>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div class="form-control">
                    <label class="label"
                      ><span class="label-text">${m.company_registration_contactPosition_label()} *</span></label
                    >
                    <input
                      type="text"
                      class="input input-bordered w-full"
                      placeholder=${m.company_registration_contactPosition_placeholder()}
                      .value=${contactPosition}
                      @input=${(e: InputEvent) => setContactPosition((e.target as HTMLInputElement).value)}
                    />
                  </div>
                  <div class="form-control">
                    <label class="label"
                      ><span class="label-text">${m.company_registration_contactEmail_label()} *</span></label
                    >
                    <input
                      type="email"
                      class="input input-bordered w-full"
                      placeholder=${m.company_registration_contactEmail_placeholder()}
                      .value=${contactEmail}
                      @input=${(e: InputEvent) => setContactEmail((e.target as HTMLInputElement).value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Info link -->
          <div class="text-center text-sm text-base-content/70">
            ${m.company_registration_info_link_text()}
            <a href="${INFO_LINK}" target="_blank" rel="noopener" class="link link-primary">${INFO_LINK}</a>
          </div>

          <!-- Terms checkbox -->
          <div class="card bg-base-100 shadow">
            <div class="card-body">
              <label class="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  class="checkbox checkbox-primary mt-1"
                  .checked=${termsAccepted}
                  @change=${(e: Event) => setTermsAccepted((e.target as HTMLInputElement).checked)}
                />
                <span class="label-text text-sm leading-relaxed">
                  Ich bestätige, dass ich das Portal ausschließlich im Namen des eingeladenen Unternehmens nutze und den
                  Zugriff nur für konkrete Besetzungsverfahren im eigenen Unternehmen verwende. Ich verpflichte mich,
                  Bewerberprofile und Lebensläufe ausschließlich über die vorgesehenen Portal-Funktionen zu sichten und
                  Auswahlentscheidungen ausschließlich im Portal vorzunehmen. Eine Speicherung, Vervielfältigung,
                  Weitergabe oder Übernahme von Bewerberdaten in externe Systeme sowie die Nutzung externer KI-Tools ist
                  unzulässig. Ich habe die
                  <a
                    href="${A.absoluteUrl('/legal/company-terms')}"
                    target="_blank"
                    rel="noopener"
                    class="link link-primary"
                    @click=${(e: Event) => e.stopPropagation()}
                    >Nutzungsbedingungen für Unternehmen</a
                  >
                  gelesen und akzeptiere sie.
                </span>
              </label>
            </div>
          </div>

          <!-- Teilnahmevereinbarung checkbox -->
          <div class="card bg-base-100 shadow">
            <div class="card-body">
              <label class="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  class="checkbox checkbox-primary mt-1"
                  .checked=${participationAgreementAccepted}
                  @change=${(e: Event) => setParticipationAgreementAccepted((e.target as HTMLInputElement).checked)}
                />
                <span class="label-text text-sm leading-relaxed">
                  Ich habe die
                  ${
                    agreementFileId
                      ? html`<a
                          href="${AdornisFile.getServeLink(agreementFileId)}"
                          target="_blank"
                          rel="noopener"
                          class="link link-primary"
                          @click=${(e: Event) => e.stopPropagation()}
                          >Teilnahmevereinbarung „Matching Week 2026, Stand ${agreementDate || '[Datum]'}“</a
                        >`
                      : html`<span class="font-medium"
                          >Teilnahmevereinbarung „Matching Week 2026, Stand ${agreementDate || '[Datum]'}“</span
                        >`
                  }
                  gelesen und stimme ihr für das von mir vertretene Unternehmen verbindlich zu. Ich bestätige, zur
                  Registrierung des Unternehmens berechtigt zu sein.
                </span>
              </label>
            </div>
          </div>

          <!-- Submit -->
          <div class="flex justify-center pb-4">
            <button
              class="btn btn-primary btn-wide ${companyRegistrationClosed ? 'btn-disabled' : ''}"
              ?disabled=${companyRegistrationClosed || submitting || !termsAccepted || !participationAgreementAccepted}
              @click=${handleSubmit}
            >
              ${submitting ? html`<span class="loading loading-spinner"></span>` : nothing}
              ${m.company_registration_submit_button()}
            </button>
          </div>
        </div>
      </div>

      <div class="py-4 border-t border-base-300">
        <div class="flex justify-center gap-4 text-sm text-base-content/70">
          <a href="${A.absoluteUrl('/privacy')}" class="hover:text-base-content transition-colors"
            >${m.privacy_policy()}</a
          >
          <a href="${A.absoluteUrl('/imprint')}" class="hover:text-base-content transition-colors">${m.imprint()}</a>
        </div>
      </div>
    </div>
  `;
});
